import {
  IFtthOrchestrator,
  IFtthReactivationOrchestrator,
  IModemReturnOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { IPayment } from "@nju/data-access-portal-payment";
import { IServiceStatus } from "@nju/data-access-portal-product";
import { IInfoResponse } from "@nju/data-access-portal-user";
import { IServiceCTABannerConditions } from "./types";

interface IProps {
  user: IInfoResponse | undefined;
  orchestrator?: IFtthOrchestrator | undefined;
  serviceStatus: IServiceStatus["ftthServiceStatus"];
  modemReturnOrchestrator: IModemReturnOrchestrator | undefined;
  reactivationOrchestratorStatus:
    | IFtthReactivationOrchestrator["state"]
    | undefined;
  hasNoProducts: boolean;
  hasUnpaidInvoices: boolean | undefined;
  payment: IPayment | undefined;
}

export function getFtthServiceCTABannerConditions({
  orchestrator,
  modemReturnOrchestrator,
  reactivationOrchestratorStatus,
  serviceStatus,
  user,
  hasNoProducts,
  hasUnpaidInvoices,
  payment,
}: IProps): IServiceCTABannerConditions {
  const isInvalidCancellationReason =
    !orchestrator?.cancellationReason ||
    (orchestrator?.cancellationReason !== "userRequest" &&
      orchestrator?.cancellationReason !== "agentRequest" &&
      orchestrator?.cancellationReason !== "NOSS_FAILED_BMK" &&
      orchestrator?.cancellationReason !== "NOSS_FAILED_BMT");

  const isBalanceLessThanPrice = !!(
    user &&
    orchestrator?.orderSummary &&
    user?.availablePaymentMethods[0]?.methodType === "DIGITAL_WALLET" &&
    Number.parseFloat(user.availablePaymentMethods[0].details.amount) <
      orchestrator.orderSummary.totalOrderPrice
  );

  const isEnoughMoney = !!(
    user &&
    orchestrator?.orderSummary &&
    user?.availablePaymentMethods[0]?.methodType === "DIGITAL_WALLET" &&
    Number.parseFloat(user.availablePaymentMethods[0].details?.amount) >=
      orchestrator.orderSummary.totalOrderPrice
  );

  return {
    isPaymentInProgress:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      user?.availablePaymentMethods[0]?.methodType === "DIGITAL_WALLET" &&
      isEnoughMoney,

    isDWPaymentPending:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      user?.availablePaymentMethods[0]?.methodType === "DIGITAL_WALLET" &&
      isBalanceLessThanPrice &&
      payment?.status === "PENDING" &&
      payment.paymentMethod.methodType === "BLIK",

    isBMPaymentPending:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      user?.availablePaymentMethods[0]?.methodType === "DIGITAL_WALLET" &&
      isBalanceLessThanPrice &&
      payment?.status === "PENDING" &&
      payment.paymentMethod.methodType === "PAY_BY_LINK",

    isPaymentError:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      user?.availablePaymentMethods[0]?.methodType === "CREDIT_CARD" &&
      payment?.status === "PENDING_REPEATING" &&
      !!orchestrator?.paymentErrorCategory,

    isTimeForPaymentWithoutProducts:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      hasNoProducts &&
      user?.availablePaymentMethods[0].methodType === "DIGITAL_WALLET" &&
      isBalanceLessThanPrice,

    isTimeForPaymentWithProducts:
      orchestrator?.state === "ORDER_PAYMENT" &&
      orchestrator.paymentStatus === "PENDING_REPEATING" &&
      serviceStatus === "PendingActive" &&
      user?.availablePaymentMethods[0].methodType === "DIGITAL_WALLET" &&
      isBalanceLessThanPrice,

    isUnsubscribed:
      orchestrator?.state === "COMPLETED" && serviceStatus === "Unsubscribed",

    isNoPayment:
      orchestrator?.state === "COMPLETED" && serviceStatus === "NoPayment",

    isAlmostTerminated:
      orchestrator?.state === "COMPLETED" &&
      serviceStatus === "AlmostTerminated" &&
      (reactivationOrchestratorStatus === undefined ||
        reactivationOrchestratorStatus === "START"),

    isDisconnected:
      orchestrator?.state === "COMPLETED" &&
      (reactivationOrchestratorStatus === undefined ||
        reactivationOrchestratorStatus === "START") &&
      serviceStatus === "Disconnected",

    isSuspended:
      orchestrator?.state === "COMPLETED" &&
      (reactivationOrchestratorStatus === undefined ||
        reactivationOrchestratorStatus === "START") &&
      serviceStatus === "Suspended",

    isPendingReactivation:
      orchestrator?.state === "COMPLETED" &&
      !!reactivationOrchestratorStatus &&
      reactivationOrchestratorStatus !== "START" &&
      (serviceStatus === "Suspended" || serviceStatus === "Disconnected"),

    isPendingTermination:
      orchestrator?.state === "COMPLETED" &&
      serviceStatus === "PendingTermination",

    isTermination:
      orchestrator?.state === "COMPLETED" &&
      hasNoProducts &&
      (modemReturnOrchestrator?.state === "RETURN_PENDING" ||
        modemReturnOrchestrator?.state === "START"),

    isPending:
      (orchestrator?.state === "COMPLETED" &&
        modemReturnOrchestrator?.state === "COMPLETED" &&
        hasNoProducts &&
        !hasUnpaidInvoices) ||
      (orchestrator?.state === "ORDER_PENDING_CANCELLATION" &&
        isInvalidCancellationReason) ||
      orchestrator?.state === "ORDER_PAYMENT_ERROR" ||
      (orchestrator?.state === "COMPLETED" &&
        (hasNoProducts || serviceStatus === "PendingCancellation")),

    haveToPaidBeforeOrder:
      hasNoProducts &&
      !!hasUnpaidInvoices &&
      (modemReturnOrchestrator === undefined ||
        modemReturnOrchestrator?.state === "COMPLETED") &&
      (orchestrator?.state === undefined ||
        orchestrator?.state === "COMPLETED"),

    isUnreturnedModem:
      orchestrator?.state === "COMPLETED" &&
      modemReturnOrchestrator?.state === "NOT_RETURNED_PENALTY",

    isOrderProcessAvailable: !orchestrator || orchestrator.state === "START",
  };
}
